import type { BroadcastMessage, UserChangeMessage } from '@/const/broadcast';
import globalEventEmitter from '@modules/EventEmitter';
import { EVENTS } from '@modules/EventEmitter/const';
import { useUserStore } from '@stores/user';
import { useCallback, useEffect, useRef } from 'react';
import { BROADCAST_EVENTS } from '@/const/broadcast';
import { useVisibilityChange } from '@/helpers/hooks/events';
import { buildLoginRedirectPath, isProjectRoutePath } from '@/modules/CustomRouter/helper';
import { RoutePath } from '@/modules/CustomRouter/type';
import { createBroadcastChannel } from '@/utils/broadcast';

/**
 * 用户刷新 Hook
 * 用于跨页面感知用户身份变化，并在页面切回前台时同步最新身份
 */
export function useUserRefresh(enabled: boolean) {
  const enabledRef = useRef(enabled);
  const pendingUserIdRef = useRef<string | null>(null);

  enabledRef.current = enabled;

  const syncUserSession = useCallback(() => {
    if (!enabledRef.current || pendingUserIdRef.current === null) {
      return;
    }

    const latestUserId = pendingUserIdRef.current;
    pendingUserIdRef.current = null;
    const { isLogin, userInfo } = useUserStore.getState();
    const currentUserId = userInfo?.userId || '';
    const { hash, pathname, search } = window.location;

    if (latestUserId === currentUserId) {
      return;
    }

    if (!latestUserId) {
      if (!isLogin) {
        return;
      }

      if (isProjectRoutePath(pathname)) {
        window.location.replace(buildLoginRedirectPath(pathname, search, hash));
        return;
      }

      window.location.reload();
      return;
    }

    // 匿名项目已在登录成功前完成 claim，匿名态登录后保留当前项目并重建会话。
    if (isLogin && isProjectRoutePath(pathname)) {
      window.location.replace(RoutePath.Workspace);
      return;
    }

    window.location.reload();
  }, []);

  useVisibilityChange(syncUserSession);

  useEffect(() => {
    const bc = createBroadcastChannel(BROADCAST_EVENTS.USER_CHANGE);
    if (!bc) {
      return;
    }

    bc.onmessage = function (ev: MessageEvent<BroadcastMessage<UserChangeMessage>>) {
      const { type, data } = ev.data;
      if (type !== BROADCAST_EVENTS.USER_CHANGE || !data) {
        return;
      }

      pendingUserIdRef.current = data.userId;
    };

    const handleUserChange = () => {
      const { userInfo } = useUserStore.getState();
      const currentUserId = userInfo?.userId || '';
      bc.postMessage({ type: BROADCAST_EVENTS.USER_CHANGE, data: { userId: currentUserId } });
    };

    globalEventEmitter.on(EVENTS.USER_CHANGE, handleUserChange);

    return () => {
      bc.close();
      globalEventEmitter.off(EVENTS.USER_CHANGE, handleUserChange);
    };
  }, []);
}
